import { useCallback, useEffect, useRef } from 'react'
import { bufferEvent, flushEvents, sendEventNow } from '@entities/analytics'
import { BANNER_SPECIAL_CODES, type BannerSpecialType } from '../lib/specialCodes'
import type { BannerRef, BannerShowSource, BannerSwitchDirection } from '../types'

/**
 * Порог видимости. Если зажать стрелку и пролететь слайдер насквозь, этих
 * баннеров пользователь не видел — а в отчёте они получили бы полноценные
 * показы.
 */
const VISIBILITY_THRESHOLD_MS = 500

/** Как опознать слайд: каталожный по id, зашитый во фронте — по коду. */
export interface TrackedBanner {
  announcementId?: number | null
  specialType?: BannerSpecialType | null
}

const toRef = (banner: TrackedBanner | undefined): BannerRef | null => {
  if (!banner) return null
  if (banner.specialType) {
    const code = BANNER_SPECIAL_CODES[banner.specialType]
    return code ? { special_code: code } : null
  }
  if (typeof banner.announcementId === 'number') {
    return { announcement_id: banner.announcementId }
  }
  return null
}

const isPageHidden = (): boolean =>
  typeof document !== 'undefined' && document.visibilityState === 'hidden'

interface UseBannerTrackerOptions {
  /** Слайды в том порядке, в каком их видит пользователь. */
  banners: TrackedBanner[]
  /** Индекс отображаемого слайда, с 0. */
  currentIndex: number
  /**
   * Слайдер отрисован и виден. Пока грузятся анонсы, на экране скелетон —
   * показ в это время засчитал бы баннер, которого пользователь ещё не видел.
   */
  enabled: boolean
}

/**
 * Аналитика слайдера баннеров.
 *
 * Показ пишется на смену отображаемого баннера и на первое появление слайдера
 * — одно правило вместо перечня сценариев. Возврат к уже показанному баннеру
 * это отдельный показ, дедуплицировать его не нужно.
 */
export const useBannerTracker = ({
  banners,
  currentIndex,
  enabled,
}: UseBannerTrackerOptions) => {
  // Причину показа выставляет тот, кто меняет слайд. Первый показ — `initial`.
  const sourceRef = useRef<BannerShowSource>('initial')
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const bannersRef = useRef(banners)
  bannersRef.current = banners

  /** Причина следующей смены слайда. Сбрасывается после того, как учтена. */
  const markSource = useCallback((source: BannerShowSource) => {
    sourceRef.current = source
  }, [])

  const refAt = useCallback(
    (index: number) => toRef(bannersRef.current[index]),
    []
  )

  useEffect(() => {
    if (timerRef.current !== null) clearTimeout(timerRef.current)

    const ref = refAt(currentIndex)
    // Пока слайдер не виден, показов нет: страница в фоне или ещё скелетон.
    if (!enabled || !ref || isPageHidden()) return

    const source = sourceRef.current
    const position = currentIndex + 1

    timerRef.current = setTimeout(() => {
      timerRef.current = null
      bufferEvent({ name: 'announcement_shown', ...ref, position, source })
    }, VISIBILITY_THRESHOLD_MS)

    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [currentIndex, enabled, refAt])

  /**
   * Нажата стрелка. Пишется на КАЖДОЕ нажатие, даже если показа не будет:
   * одно событие про действие, другое про то, что реально увидели.
   */
  const trackSwitched = useCallback(
    (index: number, direction: BannerSwitchDirection) => {
      const ref = refAt(index)
      if (!ref) return
      bufferEvent({
        name: 'announcement_switched',
        ...ref,
        position: index + 1,
        direction,
      })
    },
    [refAt]
  )

  /**
   * Клик по баннеру. Уходит немедленно отдельным запросом: клик уводит с
   * главной, и накопленный буфер в этот момент рискует не уехать вообще.
   */
  const trackClicked = useCallback(
    (index: number) => {
      const ref = refAt(index)
      if (!ref) return
      sendEventNow({
        name: 'announcement_clicked',
        ...ref,
        position: index + 1,
      })
    },
    [refAt]
  )

  // Уход с главной — последний момент, когда хвост сессии ещё можно отправить.
  useEffect(() => () => flushEvents(), [])

  return { markSource, trackSwitched, trackClicked }
}
